import { MaintenanceSpecLink } from "@/components/MaintenanceSpecLink";
import { MAINTENANCE_SPEC_FINDER } from "@/lib/maintenance-spec";
import { createFileRoute, Link } from "@tanstack/react-router";
import { EstimatedBadge } from "@/components/EstimatedBadge";
import { getMakes } from "@/lib/vehicle-queries";
import { breadcrumbLd, SITE_OG_IMAGE } from "@/lib/seo";
import { MaintenancePdfButton } from "@/components/MaintenancePdfButton";
import { MaintenanceIcsButton } from "@/components/MaintenanceIcsButton";
import { dateForMonths } from "@/lib/maintenance-ics";

const PATH = "https://autospecdata.com/car-maintenance-schedule";
const TITLE = "Car Maintenance Schedule & Checklist by Mileage | AutoSpecData";
const ANSWER =
  "A standard car maintenance schedule is: oil and filter every 7,500 miles or 12 months, tire rotation every 7,500 miles, cabin air filter every 15,000 miles, brake inspection every 15,000 miles, engine air filter and brake fluid every 30,000 miles, transmission fluid around 60,000 miles, and spark plugs and coolant near 100,000 miles.";

const CHECKLIST = [
  {
    miles: "Every 5,000–7,500 miles",
    items: [
      "Engine oil and oil filter",
      "Tire rotation and pressure check",
      "Top up washer fluid, check all lights",
      "Visual belt, hose, and leak inspection",
    ],
  },
  {
    miles: "Every 15,000 miles / 12 months",
    items: [
      "Cabin air filter",
      "Brake pad and rotor inspection",
      "12V battery load test",
      "Wiper blades",
    ],
  },
  {
    miles: "Every 30,000 miles / 3 years",
    items: [
      "Engine air filter",
      "Brake fluid flush",
      "Alignment check",
      "Suspension and steering inspection",
    ],
  },
  {
    miles: "Every 60,000 miles",
    items: [
      "Transmission fluid (40,000 miles for a CVT)",
      "Differential / transfer case fluid on AWD and 4WD",
      "Brake pads typically replaced around here",
      "Tires typically replaced between 40,000 and 60,000 miles",
    ],
  },
  {
    miles: "Every 100,000 miles",
    items: [
      "Spark plugs (iridium or platinum)",
      "Engine coolant",
      "Serpentine / accessory belt",
      "Timing belt if your engine uses one — check the manual",
    ],
  },
];

const FAQS = [
  {
    q: "What is a standard car maintenance schedule?",
    a: ANSWER,
  },
  {
    q: "How often should I change my oil?",
    a: "Most vehicles built from 2011 onward run full-synthetic oil with a 7,500-mile or 12-month interval; older engines on conventional oil are usually 5,000 miles, and diesels commonly run 10,000. Halve the interval under severe-duty use: short trips, towing, extended idling, or extreme temperatures.",
  },
  {
    q: "What maintenance does an electric car need?",
    a: "An EV has no oil, spark plugs, or exhaust to service. Its recurring items are tire rotation every 7,500 miles, brake fluid every three years, cabin air filter every 15,000 miles, and a battery and inverter coolant check around every 30,000 miles. Regenerative braking makes pads last far longer, but rotors can corrode from light use.",
  },
  {
    q: "Do I have to use the dealer to keep my warranty?",
    a: "No. In the United States, the Magnuson-Moss Warranty Act means a manufacturer cannot void your warranty simply because an independent shop or you performed the service. Keep receipts and records showing the correct parts, fluids, and intervals were used.",
  },
  {
    q: "What happens if I skip a service?",
    a: "Cabin filters and wiper blades are comfort items. Brake fluid, coolant, transmission fluid, and timing components are the services where deferral turns a routine cost into a repair bill — degraded brake fluid lowers boiling point, and neglected CVT fluid is a common cause of transmission failure.",
  },
];

const GENERIC_REMINDERS: { service: string; months: number; note: string }[] = [
  { service: "Engine oil & filter", months: 6, note: "Every 5,000–7,500 miles or 12 months." },
  { service: "Tire rotation & pressure check", months: 6, note: "Every 7,500 miles." },
  { service: "Cabin air filter", months: 12, note: "Every 15,000 miles or 12 months." },
  { service: "Brake inspection", months: 12, note: "Pads and rotors, every 15,000 miles." },
  { service: "Engine air filter", months: 24, note: "Every 30,000 miles." },
  { service: "Brake fluid flush", months: 36, note: "Every 30,000 miles or 3 years." },
  { service: "Engine coolant check", months: 36, note: "Full change near 100,000 miles." },
];

const buildGenericIcs = () => ({
  reminders: GENERIC_REMINDERS.map((r) => ({
    title: `Car maintenance — ${r.service}`,
    date: dateForMonths(r.months),
    description: `${r.note} Source: AutoSpecData car maintenance schedule.`,
  })),
  calendarName: "Car maintenance reminders",
  filename: "car-maintenance-reminders.ics",
});

export const Route = createFileRoute("/car-maintenance-schedule")({
  loader: async () => await getMakes(),
  component: MaintenanceHub,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: ANSWER.slice(0, 300) },
      { property: "og:title", content: "Car Maintenance Schedule & Checklist by Mileage" },
      { property: "og:description", content: ANSWER.slice(0, 300) },
      { property: "og:url", content: PATH },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Car Maintenance Schedule & Checklist by Mileage",
          url: PATH,
          description: ANSWER,
          inLanguage: "en-US",
          isPartOf: { "@type": "WebSite", name: "AutoSpecData", url: "https://autospecdata.com/" },
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", "[data-quick-answer]"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Car Maintenance Schedule", path: PATH },
          ]),
        ),
      },
    ],
  }),
});

function MaintenanceHub() {
  const { makes } = Route.useLoaderData();

  return (
    <div>
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Car Maintenance Schedule</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            Car Maintenance Schedule &amp; Checklist by Mileage
          </h1>
          <div className="mt-3"><EstimatedBadge /></div>
          <p
            data-quick-answer
            className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground"
          >
            {ANSWER}
          </p>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
            Below is the general schedule. For intervals adjusted to your exact
            powertrain, transmission, and drivetrain, open your model's maintenance page.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <MaintenancePdfButton
              label="Download the checklist as PDF"
              build={() => ({
                title: "Car Maintenance Schedule & Checklist",
                subtitle: "Printable checklist by mileage",
                intro: ANSWER,
                filename: "car-maintenance-checklist.pdf",
                sections: CHECKLIST.map((b) => ({
                  heading: b.miles,
                  items: b.items.map((i) => ({ label: i })),
                })),
              })}
            />
            <MaintenanceIcsButton build={buildGenericIcs} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-foreground">
            Car maintenance checklist
          </h2>
          <MaintenancePdfButton
            secondary
            label="Print / save as PDF"
            build={() => ({
              title: "Car Maintenance Schedule & Checklist",
              subtitle: "Printable checklist by mileage",
              intro: ANSWER,
              filename: "car-maintenance-checklist.pdf",
              sections: CHECKLIST.map((b) => ({
                heading: b.miles,
                items: b.items.map((i) => ({ label: i })),
              })),
            })}
          />
          <MaintenanceIcsButton label="Calendar reminders (.ics)" build={buildGenericIcs} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CHECKLIST.map((block) => (
            <div key={block.miles} className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-semibold text-primary mb-3">{block.miles}</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {block.items.map((i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-primary">•</span>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Your owner's manual is the authority. Follow its severe-duty schedule if you
          tow, idle often, or drive mostly short trips.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
        <h2 className="text-xl font-bold text-foreground mb-4">
          Maintenance schedules by make
        </h2>
        <div className="flex flex-wrap gap-2">
          {makes.map((m: { id: string; name: string; slug: string }) => (
            <Link
              key={m.id}
              to="/$make/maintenance-schedule"
              params={{ make: m.slug }}
              className="px-3 py-1.5 rounded-full border border-border text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              {m.name} maintenance schedule
            </Link>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Compare service intervals across brands on the{" "}
          <Link to="/maintenance-comparison" className="text-primary underline">
            maintenance interval comparison
          </Link>{" "}
          page, or read{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "maintenance-intervals-that-matter" }}
            className="text-primary underline"
          >
            which maintenance intervals actually matter
          </Link>
          . For the single most-skipped service, see the{" "}
          <Link to="/transmission-fluid-change-interval" className="text-primary underline">
            transmission fluid change interval guide
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-8">
        <MaintenanceSpecLink
          heading="Need the full manufacturer schedule?"
          body="MaintenanceSpec provides source-backed maintenance schedules by vehicle, including mileage, time and condition-based service rules."
          anchor="Find a maintenance schedule"
          href={MAINTENANCE_SPEC_FINDER}
        />
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12 border-t border-border">
        <h2 className="text-xl font-bold text-foreground mb-4">
          Car maintenance FAQ
        </h2>
        <div className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-foreground mb-1">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
